import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw, History } from "lucide-react";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const d = (s) => (s ? String(s).slice(0, 16).replace("T", " ") : "—");
export const STATUS = {
  payment_pending: ["Payment pending", "bg-amber-100 text-amber-700"],
  paid_verified: ["Payment verified", "bg-sky-100 text-sky-700"],
  activating: ["Activating", "bg-sky-100 text-sky-700"],
  paid_activation_failed: ["Paid · activation pending", "bg-orange-100 text-orange-700"],
  activated: ["Activated", "bg-emerald-100 text-emerald-700"],
  scheduled: ["Scheduled", "bg-violet-100 text-violet-700"],
  failed: ["Failed", "bg-rose-100 text-rose-700"],
  cancelled: ["Cancelled", "bg-slate-100 text-slate-600"],
  expired: ["Expired", "bg-slate-100 text-slate-600"],
};
const RETRY = ["payment_pending", "paid_verified", "paid_activation_failed"];

export function PlanChangeHistory({ canEdit = true, onChanged }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["plan-history"], queryFn: async () => (await api.get("/saas/plan-change/history")).data });
  const rec = useMutation({
    mutationFn: (id) => api.post("/saas/plan-change/reconcile", { attempt_id: id }),
    onSuccess: () => { toast.success("Payment status refreshed"); qc.invalidateQueries({ queryKey: ["plan-history"] }); onChanged?.(); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const items = data?.items || [];
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto mb-6" data-testid="plan-history">
      {items.length ? (
        <table className="w-full text-sm" data-testid="plan-history-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500"><tr>
            <th className="px-3 py-3">Requested</th><th className="px-3 py-3">Change</th><th className="px-3 py-3">Period</th>
            <th className="px-3 py-3 text-right">Base + GST = Total</th><th className="px-3 py-3">Status</th>
            <th className="px-3 py-3">Razorpay</th><th className="px-3 py-3">Verified / Activated</th><th className="px-3 py-3">By</th><th className="px-3 py-3" /></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((h) => {
              const [label, cls] = STATUS[h.status] || [h.status, "bg-slate-100 text-slate-600"];
              return (
                <tr key={h.id} data-testid={`plan-history-row-${h.id}`} className="align-top">
                  <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap">{d(h.created_at)}</td>
                  <td className="px-3 py-3"><span className="text-slate-500">{h.previous_plan_name || "—"}</span> → <span className="font-semibold">{h.plan_name}</span>
                    {h.kind === "admin_override" && <div className="mt-0.5 text-[11px] font-semibold text-violet-700" data-testid={`plan-history-override-${h.id}`}>Administrative Override{h.reason ? ` · ${h.reason}` : ""}</div>}
                    {h.failure_reason && <div className="mt-0.5 text-[11px] text-rose-600" data-testid={`plan-history-reason-${h.id}`}>{h.failure_reason}</div>}</td>
                  <td className="px-3 py-3 capitalize">{h.billing_interval}</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">{inr(h.base_amount)} + {inr(h.tax_amount)} = <b>{inr(h.amount)}</b> <span className="text-xs text-slate-400">{h.currency}</span></td>
                  <td className="px-3 py-3"><span data-testid={`plan-history-status-${h.id}`} className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>{label}</span></td>
                  <td className="px-3 py-3 font-mono text-[11px] text-slate-500">{h.provider_order_id || "—"}<br />{h.provider_payment_id || ""}</td>
                  <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap">{d(h.payment_verified_at)}<br />{d(h.activated_at)}</td>
                  <td className="px-3 py-3 text-xs">{h.initiated_by || "—"}</td>
                  <td className="px-3 py-3">{canEdit && RETRY.includes(h.status) && (
                    <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" disabled={rec.isPending} data-testid={`plan-history-recheck-${h.id}`} onClick={() => rec.mutate(h.id)}><RefreshCw className="h-3 w-3" /> Check status</Button>)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : <div className="p-8 text-center text-sm text-slate-400" data-testid="plan-history-empty"><History className="mx-auto h-6 w-6 text-slate-300 mb-2" /> No plan changes yet</div>}
    </div>
  );
}
